import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { backendUrl } from '../utils/backendUrl';
import UserTimezoneSelect from './UserTimezoneSelect';
import PasswordHint from './PasswordHint';
import './Login.css';

const PasswordReset = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [email, setEmail] = useState('');
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setError('');
        setSuccess('');
    }, [token]);

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            const response = await axios.post(backendUrl('/auth/forgot-password'), { email }, { withCredentials: true });
            setSuccess(response.data?.message
                || "If an account exists for this email address, we've sent a password reset link.");
            setEmail('');
        } catch (_err) {
            setSuccess("If an account exists for this email address, we've sent a password reset link.");
        } finally {
            setLoading(false);
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (formData.newPassword !== formData.confirmPassword) {
            setError('New passwords do not match');
            return;
        }
        if (formData.newPassword.length < 6) {
            setError('New password must be at least 15 characters');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(backendUrl('/auth/reset-password'), {
                token,
                newPassword: formData.newPassword,
                confirmPassword: formData.confirmPassword
            }, { withCredentials: true });
            setSuccess(response.data?.message || 'Password reset successfully. You can sign in with your new password.');
            setFormData({ newPassword: '', confirmPassword: '' });
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            setError(err.response?.data?.error || 'This reset link is invalid or has expired.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-box">
                <h2>{token ? 'Set a new password' : 'Reset Password'}</h2>
                <p className="subtitle">
                    {token
                        ? 'Choose a new password for your Commray account'
                        : "Enter your email and we'll send a reset link if an account exists."}
                </p>

                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                <div className="form-group">
                    <label htmlFor="commray-password-reset-tz">Time zone</label>
                    <UserTimezoneSelect showInnerLabel={false} selectId="commray-password-reset-tz" embedded />
                </div>

                {token ? (
                    <form onSubmit={handleSetPassword}>
                        <div className="form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <input
                                type="password"
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={(e) => setFormData((f) => ({ ...f, newPassword: e.target.value }))}
                                required
                                disabled={loading}
                                placeholder="At least 15 characters"
                            />
                            <PasswordHint value={formData.newPassword} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData((f) => ({ ...f, confirmPassword: e.target.value }))}
                                required
                                disabled={loading}
                                placeholder="Confirm your new password"
                            />
                        </div>
                        <button type="submit" disabled={loading} className="login-button">
                            {loading ? 'Saving…' : 'Reset Password'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleRequestReset}>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                placeholder="you@company.com"
                            />
                        </div>
                        <button type="submit" disabled={loading} className="login-button">
                            {loading ? 'Sending…' : 'Send reset link'}
                        </button>
                    </form>
                )}

                <div className="signup-link">
                    <p><Link to="/login">Back to login</Link></p>
                </div>
            </div>
        </div>
    );
};

export default PasswordReset;
